import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CloseReason,
  IceConfig,
  ParticipantView,
  PeerMessage,
  TranslatorState,
} from '@voxa/shared';
import { api, ApiError } from './api.ts';
import { PeerLink, type PeerStatus } from './peer.ts';
import { Signaling, type SignalingStatus } from './signaling.ts';
import { OpenAITranslator } from './translator/openai.ts';
import { ElevenLabsVoice } from './translator/elevenlabs.ts';
import { NO_LEAD, VoiceRace, listenForOnsets, type LeadStats } from './translator/lead.ts';
import { CallParts } from './translator/parts.ts';
import { IdleSwitch, listenForVoice } from './translator/idle.ts';
import { ProviderEventRecon } from './translator/recon.ts';
import type { TranslatorMetrics } from './translator/types.ts';
import { TranslatorError, failureFromApi, type TranslatorFailure } from './translator/failures.ts';
import { telemetry } from './telemetry.ts';
import { capabilitiesNow } from './capabilities.ts';
import { outbox } from './outbox.ts';
import { NO_USAGE, type Usage } from './cost.ts';

/**
 * One call, start to finish. Holds the three moving parts — the signalling
 * socket, the peer connection and this side's translator — and exposes a flat
 * view model, so no component has to know the order they come up in.
 *
 * The routing rule that everything else serves: the microphone goes to the
 * peer, the peer's original track goes to the translator, and the translator's
 * output goes to the speakers. Nothing else is connected to anything.
 */

export type CallPhase = 'idle' | 'connecting' | 'live' | 'ended';

/** Section 8: past this much drift, say so instead of pretending. */
const LAG_WARNING_MS = 3000;
/** How often each side's usage is counted and shared for the running cost. */
const USAGE_EVERY_MS = 5_000;
/** Subtitles live in the tab and are trimmed; they are never persisted. */
const SUBTITLE_LIMIT = 4000;

export interface CallView {
  phase: CallPhase;
  signaling: SignalingStatus;
  peer: PeerStatus;
  translator: TranslatorState;
  /** Why the translator failed, as a code the UI turns into words. */
  translatorFailure: TranslatorFailure | null;
  peerPresent: boolean;
  peerTranslator: TranslatorState;
  peerMicMuted: boolean;
  micMuted: boolean;
  callStartedAt: number | null;
  callEndsAt: number | null;
  closeReason: CloseReason | null;
  /** True when the translator is measurably trailing. */
  lagging: boolean;
  /** Set when the P2P route is running through TURN. */
  relayed: boolean;
  /**
   * Grouped by who is speaking, because that is how a conversation reads.
   *
   * Each side only translates the other, so `peer` comes from this browser's
   * translator and `me` arrives over the peer connection from theirs. Without
   * that exchange a participant could see the other person's words but never
   * their own.
   */
  subtitles: {
    peer: { original: string; translated: string };
    me: { original: string; translated: string };
  };
  metrics: TranslatorMetrics;
  /**
   * What the second voice adds on top of the translation: from a phrase being
   * sent to it to its first sound. Null when this seat hears the built-in
   * voice, or before the first phrase.
   */
  voiceDelayMs: number | null;
  /** Set when this seat's second voice failed and it went back to the built-in one. */
  voiceFellBack: boolean;
  /**
   * How much earlier ElevenLabs was heard than the built-in voice would have
   * been, utterance by utterance, on a seat that hears ElevenLabs. See
   * `translator/lead.ts` for what is timed against what.
   */
  voiceLead: LeadStats;
  /**
   * What each side's providers have been used for so far, for the creator's
   * running cost. `peer` arrives over the peer connection from their browser.
   */
  usage: { me: Usage; peer: Usage };
  /**
   * The signalling socket gave up inside its retry budget. Distinct from
   * `closeReason`: nobody closed the room, we simply lost the way to it.
   */
  signalLost: boolean;
  /** A server-sent signalling error, verbatim. Rare, and not localised. */
  error: string | null;
}

type Speaker = 'peer' | 'me';
type Line = 'original' | 'translated';

/** Appends to one line of one speaker's pane, trimmed to the tab-only budget. */
function appendSubtitle(
  subtitles: CallView['subtitles'],
  speaker: Speaker,
  line: Line,
  text: string,
): CallView['subtitles'] {
  const grown = (subtitles[speaker][line] + text).slice(-SUBTITLE_LIMIT);
  return { ...subtitles, [speaker]: { ...subtitles[speaker], [line]: grown } };
}

const emptySubtitles = (): CallView['subtitles'] => ({
  peer: { original: '', translated: '' },
  me: { original: '', translated: '' },
});

/** Whatever was thrown, reduced to something the UI has words for. */
function toFailure(err: unknown): TranslatorFailure {
  if (err instanceof TranslatorError) return err.failure;
  if (err instanceof ApiError) return failureFromApi(err.code, err.status);
  return { code: 'unknown' };
}

const emptyMetrics: TranslatorMetrics = {
  connectMs: null,
  firstTranslationMs: null,
  estimatedLagMs: null,
  rttMs: null,
  jitterMs: null,
  packetsLost: null,
  playoutDelayMs: null,
  audioPlayedMs: null,
  acceleratedMs: null,
  stretchedMs: null,
};

const initialView: CallView = {
  phase: 'idle',
  signaling: 'connecting',
  peer: 'new',
  translator: 'idle',
  translatorFailure: null,
  peerPresent: false,
  peerTranslator: 'idle',
  peerMicMuted: false,
  micMuted: false,
  callStartedAt: null,
  callEndsAt: null,
  closeReason: null,
  lagging: false,
  relayed: false,
  subtitles: {
    peer: { original: '', translated: '' },
    me: { original: '', translated: '' },
  },
  metrics: emptyMetrics,
  voiceDelayMs: null,
  voiceFellBack: false,
  voiceLead: NO_LEAD,
  usage: { me: NO_USAGE, peer: NO_USAGE },
  signalLost: false,
  error: null,
};

export interface UseCallOptions {
  participant: ParticipantView;
  micStream: MediaStream | null;
}

export function useCall({ participant, micStream }: UseCallOptions) {
  const [view, setView] = useState<CallView>(initialView);
  const patch = useCallback(
    (next: Partial<CallView>) => setView((prev) => ({ ...prev, ...next })),
    [],
  );

  const signalingRef = useRef<Signaling | null>(null);
  const peerRef = useRef<PeerLink | null>(null);
  const translatorRef = useRef<OpenAITranslator | null>(null);
  /** The second voice speaking this seat's translation, when the room chose one. */
  const voiceRef = useRef<ElevenLabsVoice | null>(null);
  /**
   * Every translator and voice this call has had, including the ones a sleep
   * or a restart closed. Their counters freeze when they stop, so summing them
   * all is the call's usage with nothing counted twice.
   */
  /** One race for the whole call, so its average spans restarts and sleeps. */
  const raceRef = useRef<VoiceRace | null>(null);
  const usedRef = useRef<{ translators: OpenAITranslator[]; voices: ElevenLabsVoice[] }>({
    translators: [],
    voices: [],
  });
  const remoteTrackRef = useRef<MediaStreamTrack | null>(null);
  const iceRef = useRef<IceConfig>(participant.iceConfig);
  const isInitiatorRef = useRef<boolean>(participant.seat === 0);
  const startedRef = useRef(false);
  const peerWasPresentRef = useRef(false);
  /** The peer's translator session their subtitles currently belong to. */
  const peerSessionRef = useRef<string | null>(null);
  /** The backup switch of the current translator; see `translator/idle.ts`. */
  const idleRef = useRef<IdleSwitch | null>(null);
  /** Stops listening for the peer's voice while the translator is asleep. */
  const stopListeningRef = useRef<(() => void) | null>(null);
  /** Lets a sleeping translator wake itself without a dependency cycle. */
  const wakeRef = useRef<() => void>(() => undefined);

  const [originalStream, setOriginalStream] = useState<MediaStream | null>(null);
  const [translatedStream, setTranslatedStream] = useState<MediaStream | null>(null);

  const roomId = participant.roomId;
  /** What I hear; also what my translator produces. */
  const myLanguage = participant.myLanguage;
  /** What the peer speaks; also what my translator is fed. */
  const peerLanguage = participant.peerLanguage;

  /** Tell the room what this side's translator is doing; the clock depends on it. */
  const reportTranslator = useCallback((state: TranslatorState) => {
    signalingRef.current?.send({ type: 'translator-state', state });
  }, []);

  /** Everything the sleep machinery owns, stopped. Idempotent. */
  const stopIdleWatch = useCallback(() => {
    idleRef.current?.stop();
    idleRef.current = null;
    stopListeningRef.current?.();
    stopListeningRef.current = null;
  }, []);

  const startTranslator = useCallback(
    async (inputTrack: MediaStreamTrack, resumed = false) => {
      stopIdleWatch();
      translatorRef.current?.close();
      voiceRef.current?.close();
      voiceRef.current = null;

      // Set when the idle switch, not the call, is what closed this session.
      // It changes what the `closed` below is shown as — nothing else.
      let sleeping = false;

      // Task 1, and off unless this server says a recon run is on. The flag is
      // read without waiting on it (see `capabilitiesNow`): a debugging aid
      // may not put a network round trip in front of a call. Its reports are
      // ordinary telemetry — numbers and short tags, never a transcript — and
      // the adapter queues the last one during its own shutdown, which happens
      // before the state change below, so the flush that closes the accounting
      // row carries the summary out with it.
      const recon = capabilitiesNow()?.recon?.providerEvents
        ? new ProviderEventRecon({
            report: (batch) => {
              for (const r of batch) telemetry.record(r.name, r.values, r.tags);
            },
          })
        : null;

      // Task 2. One per attempt, like the adapter: parts are numbered within a
      // session, and a restart is a new session.
      //
      // The reports go out on the outbox rather than on telemetry — they are
      // rows, not metrics, and the outbox is the channel that survives the tab
      // closing. Nothing here is awaited: `part()` is a push onto an array.
      const parts = new CallParts({
        // Whether this room paid for source transcription decides what a part
        // can even be built from. Task 13: without it, refusing to open a part
        // measured nothing at all.
        expectInput: participant.sourceSubtitles,
        onPart: (part) =>
          outbox.part({
            seq: part.seq,
            // Which way this session translates. Redundant with the session
            // row today and cheap to carry, and the day a provider translates
            // both ways at once it is the only place that could say so.
            direction: `${peerLanguage} -> ${myLanguage}`,
            boundary: part.boundary,
            startedAt: part.startedAt,
            ...(part.firstOutputMs !== undefined && { firstOutputMs: part.firstOutputMs }),
            durationMs: part.durationMs,
          }),
      });

      // Task 6. When this seat's voice is ElevenLabs, the translator still
      // translates and its text is spoken again in that voice; its own audio
      // is kept, unplayed, as the way back. One per attempt, like everything
      // else here, so a restart or a wake starts it clean.
      let builtInStream: MediaStream | null = null;
      const holder: { translator: OpenAITranslator | null } = { translator: null };
      // Timing the two voices against each other, only while the second one
      // is the one speaking. The built-in voice is listened to, not played.
      raceRef.current ??= new VoiceRace();
      const race = raceRef.current;
      let stopOnsets: (() => void) | null = null;
      const stopRace = () => {
        stopOnsets?.();
        stopOnsets = null;
      };
      const voice =
        participant.voice?.provider === 'elevenlabs'
          ? new ElevenLabsVoice({
              fetchSession: () => api.voiceSession(roomId),
              onFailed: (reason) => {
                // Back to the built-in voice rather than silence. Recorded,
                // because a second voice that quietly never speaks would make
                // every comparison between the two meaningless.
                telemetry.record('voice-failed', undefined, { reason });
                stopRace();
                if (voiceRef.current === voice) voiceRef.current = null;
                voice?.close();
                patch({ voiceDelayMs: null, voiceFellBack: true });
                if (translatorRef.current === holder.translator && builtInStream) {
                  setTranslatedStream(builtInStream);
                }
              },
              onMetrics: (m) => patch({ voiceDelayMs: m.voiceDelayMs }),
              onSpeech: (textAt, at) => {
                if (voiceRef.current !== voice) return;
                const lead = race.eleven(textAt, at);
                if (lead) patch({ voiceLead: lead });
              },
            })
          : null;
      voiceRef.current = voice;
      if (voice) {
        usedRef.current.voices.push(voice);
        setTranslatedStream(voice.stream);
      }

      // A fresh adapter per attempt. Section 8: after a break, a new session
      // id, and events from the old one are ignored — here they are simply
      // delivered to an object nothing is listening to any more.
      const translator = new OpenAITranslator(
        {
          onState: (state, failure) => {
            const shown: TranslatorState = sleeping && state === 'closed' ? 'asleep' : state;
            patch({ translator: shown, translatorFailure: failure ?? null });
            reportTranslator(shown);
            telemetry.record('translator-state', undefined, { state: shown });

            // A minute of nobody speaking is counted from the moment there is
            // something to hear, and forgotten as soon as there is not.
            if (state === 'ready') idleRef.current?.activity();
            if ((state === 'closed' || state === 'failed') && !sleeping) {
              if (translatorRef.current === translator) idleRef.current?.stop();
            }

            // How long we held a provider session open. Recorded the instant it
            // ends, and flushed rather than queued, because the end of the last
            // session is often the last thing this page does — and an unbilled
            // guess is exactly what made a runaway session invisible before.
            if (state === 'closed' || state === 'failed') {
              // The voice speaks what this session translates; it has nothing
              // left to say once the session is gone.
              stopRace();
              if (voice) {
                const lead = race.stats;
                telemetry.record('voice-session', {
                  voiceDelayMs: voice.metrics.voiceDelayMs ?? -1,
                  phrases: voice.metrics.phrasesSent,
                  chars: voice.metrics.charsSent,
                  // Whether phrases queued behind one another, and how much
                  // silence was cut to stop them doing so.
                  trimmedMs: voice.metrics.trimmedMs,
                  queueMaxMs: voice.metrics.queueMaxMs,
                  // Call-to-date rather than this session's: the race spans
                  // restarts, and an average over one short session says less.
                  leadAvgMs: lead.avgMs ?? -1,
                  leadLastMs: lead.lastMs ?? -1,
                  leadCount: lead.count,
                });
                voice.close();
                if (voiceRef.current === voice) voiceRef.current = null;
              }
              telemetry.record(
                'translator-session',
                {
                  seconds: translator.sessionSeconds,
                  // The session's own summary numbers, sent with the ending
                  // rather than left in the adapter, because this event is the
                  // last thing the page is guaranteed to send and section 10.1
                  // wants all three of them against the same session.
                  connectMs: translator.metrics.connectMs ?? -1,
                  firstTranslationMs: translator.metrics.firstTranslationMs ?? -1,
                  estimatedLagMs: translator.metrics.estimatedLagMs ?? -1,
                  // What the listener actually heard, as opposed to what was
                  // written. -1 keeps the "not measured" convention the other
                  // three use, since the channel takes numbers only.
                  playoutDelayMs: translator.metrics.playoutDelayMs ?? -1,
                  audioPlayedMs: translator.metrics.audioPlayedMs ?? -1,
                  acceleratedMs: translator.metrics.acceleratedMs ?? -1,
                  stretchedMs: translator.metrics.stretchedMs ?? -1,
                },
                // How it ended, so a long session can be told apart from a long
                // *broken* session without reading the surrounding log — and a
                // session the idle switch closed from one the call closed.
                { state, reason: failure?.code ?? (sleeping ? 'idle' : '') },
              );
              void telemetry.flush();
            }
          },
          onTranscript: (chunk) => {
            idleRef.current?.activity();
            if (chunk.direction === 'output' && voice && voiceRef.current === voice) {
              // Before `feed`, so both are stamped on the same clock and the
              // race sees the utterance open before any of it is spoken.
              race.text(performance.now());
              voice.feed(chunk.text);
            }
            // This translator listens to the peer, so both of its streams belong
            // to the peer's pane: what they said, and what I am hearing it as.
            const line: Line = chunk.direction === 'input' ? 'original' : 'translated';
            setView((prev) => ({
              ...prev,
              subtitles: appendSubtitle(prev.subtitles, 'peer', line, chunk.text),
            }));

            // The peer's screen cannot show my half without being told, and the
            // only path that keeps speech away from the server is this one.
            peerRef.current?.sendMessage({
              type: 'transcript',
              kind: chunk.direction === 'input' ? 'original' : 'translation',
              text: chunk.text,
              language: chunk.direction === 'input' ? peerLanguage : myLanguage,
              sessionId: translator.sessionId,
              ...(resumed && { resumed: true }),
              at: Date.now(),
            });
          },
          onTranslatedStream: (stream) => {
            builtInStream = stream;
            // Only heard while no second voice is speaking for this seat.
            if (!voice || voiceRef.current !== voice) {
              setTranslatedStream(stream);
              return;
            }
            stopOnsets ??= listenForOnsets(stream, (at) => {
              if (voiceRef.current !== voice) return;
              const lead = race.builtIn(at);
              if (lead) patch({ voiceLead: lead });
            });
          },
          onMetrics: (metrics) => {
            patch({
              metrics,
              lagging: (metrics.estimatedLagMs ?? 0) > LAG_WARNING_MS,
            });
          },
        },
        // From the room, not from the server's capabilities: the capability is
        // only the ceiling, and this room may have chosen not to use it. The
        // session was minted from the same record, so the two cannot disagree.
        { recon, parts, sourceSubtitles: participant.sourceSubtitles },
      );
      translatorRef.current = translator;
      holder.translator = translator;
      usedRef.current.translators.push(translator);

      // The backup switch (task 3): a minute with no word in either direction
      // closes this session, and the peer's voice reopens a new one.
      idleRef.current = new IdleSwitch(() => {
        if (translatorRef.current !== translator) return;
        sleeping = true;
        translator.close();
        translatorRef.current = null;
        telemetry.record('translator-asleep');
        stopListeningRef.current = listenForVoice(inputTrack, () => {
          stopListeningRef.current = null;
          wakeRef.current();
        });
      });

      try {
        const session = await api.translationSession(roomId);
        telemetry.bind(roomId, session.sessionId);
        outbox.bind(roomId, session.sessionId);
        await translator.connect(inputTrack, session.targetLanguage, {
          sdpUrl: session.sdpUrl,
          model: session.model,
          sessionId: session.sessionId,
          expiresAt: session.expiresAt,
        });
        telemetry.record('translator-connected', {
          connectMs: translator.metrics.connectMs ?? -1,
        });
      } catch (err) {
        // The adapter already put a specific reason on the state; keeping it
        // matters more than a tidy generic sentence, because the two
        // interesting cases — this network cannot reach the provider, and the
        // provider refused the session — need completely different fixes.
        patch({ translator: 'failed', translatorFailure: toFailure(err) });
        reportTranslator('failed');
      }
    },
    [myLanguage, patch, peerLanguage, reportTranslator, roomId, stopIdleWatch],
  );

  /**
   * A new session after a sleep. Unlike a restart, the subtitles stay: nothing
   * was cut off mid-sentence, there was a minute of silence before it.
   */
  const wakeTranslator = useCallback(() => {
    const track = remoteTrackRef.current;
    if (!track || translatorRef.current) return;
    telemetry.record('translator-wake');
    void startTranslator(track, true);
  }, [startTranslator]);
  wakeRef.current = wakeTranslator;

  /** Section 8: an explicit restart, with the unfinished fragment lost. */
  const restartTranslator = useCallback(() => {
    const track = remoteTrackRef.current;
    if (!track) return;
    // Old subtitles belong to the old session; keeping them next to new audio
    // would read as speech that never happened.
    patch({ subtitles: emptySubtitles(), lagging: false });
    telemetry.record('translator-restart');
    void startTranslator(track);
  }, [patch, startTranslator]);

  const leave = useCallback(async () => {
    signalingRef.current?.send({ type: 'leave' });
    try {
      await api.leaveRoom(roomId);
    } catch {
      // The room may already be gone; leaving twice is not a failure.
    }
  }, [roomId]);

  const toggleMic = useCallback(() => {
    setView((prev) => {
      const muted = !prev.micMuted;
      for (const track of micStream?.getAudioTracks() ?? []) track.enabled = !muted;
      signalingRef.current?.send({ type: 'mic', muted });
      return { ...prev, micMuted: muted };
    });
  }, [micStream]);

  // Everything is wired once, when the microphone is ready. Running this twice
  // would open a second socket for the same seat and take over our own call.
  useEffect(() => {
    if (!micStream || startedRef.current) return;
    startedRef.current = true;
    patch({ phase: 'connecting' });
    telemetry.bind(roomId);
    telemetry.start();
    // The background channel is opened alongside, and stays empty: what will
    // fill it is tasks 5 and 6, and both wait on the consent screen of task 8.
    // Starting it here is what makes its unload handling real — by the time
    // there is something to send, the listeners that get the last batch off a
    // closing page are already attached.
    outbox.bind(roomId);
    outbox.start();

    const peer = new PeerLink(iceRef.current, isInitiatorRef.current, {
      onRemoteTrack: (track, stream) => {
        remoteTrackRef.current = track;
        setOriginalStream(stream);
        telemetry.record('remote-track');
        // The translator can only start once there is something to translate.
        void startTranslator(track);
      },
      onPeerMessage: (message: PeerMessage) => {
        if (message.type === 'usage') {
          const { type: _type, ...peerUsage } = message;
          setView((prev) => ({ ...prev, usage: { ...prev.usage, peer: peerUsage } }));
          return;
        }

        // A new session id on their side means they restarted their
        // translator. Section 8 forbids presenting stale speech next to fresh
        // audio, so the old half-sentence goes rather than being grown.
        // A session that woke from a silence is new too, but cut nothing off,
        // so it is the one change of id that keeps what is on screen.
        const restarted =
          peerSessionRef.current !== null &&
          peerSessionRef.current !== message.sessionId &&
          !message.resumed;
        peerSessionRef.current = message.sessionId;

        // Their translator listens to me, so their `original` is my voice and
        // their `translation` is what they are hearing. Both belong in my pane.
        const line: Line = message.kind === 'original' ? 'original' : 'translated';
        setView((prev) => {
          const base = restarted
            ? { ...prev.subtitles, me: { original: '', translated: '' } }
            : prev.subtitles;
          return { ...prev, subtitles: appendSubtitle(base, 'me', line, message.text) };
        });
      },
      onLocalDescription: (description) => signalingRef.current?.send({ type: 'sdp', description }),
      onLocalCandidate: (candidate) => signalingRef.current?.send({ type: 'ice', candidate }),
      onStatus: (status) => {
        patch({ peer: status });
        if (status === 'connected') {
          void peer.stats().then((s) => patch({ relayed: s.route === 'relay' }));
        }
      },
    });
    peerRef.current = peer;
    void peer.setMicrophoneTrack(micStream.getAudioTracks()[0] ?? null);

    const signaling = new Signaling(roomId, {
      onStatus: (status) => patch({ signaling: status }),
      onGiveUp: () =>
        patch({
          phase: 'ended',
          signalLost: true,
        }),
      onMessage: (message) => {
        switch (message.type) {
          case 'room-state': {
            isInitiatorRef.current = message.state.isInitiator;

            // The offer has to be made when there is someone to receive it.
            // The one the constructor triggered went out before the peer had
            // a socket, so this transition — and only this one — is what
            // actually starts negotiation, on the first join and after a
            // reconnect alike.
            const present = message.state.peer.present;
            if (present && !peerWasPresentRef.current && message.state.isInitiator) {
              void peer.start();
            }
            peerWasPresentRef.current = present;

            patch({
              peerPresent: message.state.peer.present,
              peerTranslator: message.state.peer.translator,
              peerMicMuted: message.state.peer.micMuted,
              callStartedAt: message.state.callStartedAt,
              callEndsAt: message.state.callEndsAt,
            });
            break;
          }

          case 'sdp':
            void peer.acceptDescription(message.description);
            break;

          case 'ice':
            void peer.acceptCandidate(message.candidate);
            break;

          case 'call-started':
            patch({ phase: 'live', callStartedAt: message.startedAt, callEndsAt: message.endsAt });
            telemetry.record('call-started');
            break;

          case 'ice-config':
            iceRef.current = message.iceConfig;
            peer.updateIceServers(message.iceConfig);
            break;

          case 'room-closed':
            patch({ phase: 'ended', closeReason: message.reason });
            telemetry.record('room-closed', undefined, { reason: message.reason });
            break;

          case 'error':
            patch({ error: message.message });
            break;

          case 'pong':
            break;
        }
      },
    });
    signalingRef.current = signaling;
    signaling.connect();

    // Task 6: this side's usage, every few seconds, to this screen and to the
    // other browser, whose creator's screen adds the two for a running cost.
    // Cumulative, so a frame lost on the way is put right by the next one.
    const usageTimer = setInterval(() => {
      const used = usedRef.current;
      const translateSeconds = used.translators.reduce((n, tr) => n + tr.sessionSeconds, 0);
      const me: Usage = {
        translateSeconds,
        // Billed for the same open seconds, when the room asked for it.
        transcribeSeconds: participant.sourceSubtitles ? translateSeconds : 0,
        voiceChars: used.voices.reduce((n, v) => n + v.metrics.charsSent, 0),
      };
      setView((prev) => ({ ...prev, usage: { ...prev.usage, me } }));
      peerRef.current?.sendMessage({ type: 'usage', ...me });
    }, USAGE_EVERY_MS);

    return () => {
      clearInterval(usageTimer);
      // Section 13: leaving must free the audio resources, not merely hide the
      // screen. The microphone stream itself belongs to the caller.
      stopIdleWatch();
      translatorRef.current?.close();
      translatorRef.current = null;
      voiceRef.current?.close();
      voiceRef.current = null;
      peerRef.current?.close();
      peerRef.current = null;
      signalingRef.current?.close();
      signalingRef.current = null;
      telemetry.stop();
      outbox.stop();
    };
  }, [micStream, patch, roomId, startTranslator, stopIdleWatch]);

  /**
   * The other half of section 13, for the ending that is not an unmount.
   *
   * A call can finish — time limit, peer gone, room closed — while this hook
   * stays mounted: the screen swaps to "the call is over" and sits there until
   * someone leaves it, so the cleanup above has not run. The provider session
   * this browser opened would still be open, still fed by the peer connection,
   * and still billing, behind a screen that says the call ended. Our server
   * cannot close it for us; the audio path goes straight from here to the
   * provider. So the release happens here, when the call ends, not when the
   * page eventually goes away.
   *
   * The signalling socket is left alone: the server closes it with 1000 on
   * room-closed, and Signaling treats that as permanent.
   */
  useEffect(() => {
    if (view.phase !== 'ended') return;
    // First, or a voice heard during the goodbye would open a fresh session.
    stopIdleWatch();
    translatorRef.current?.close();
    translatorRef.current = null;
    voiceRef.current?.close();
    voiceRef.current = null;
    peerRef.current?.close();
    peerRef.current = null;
    remoteTrackRef.current = null;
    setTranslatedStream(null);
    setOriginalStream(null);
  }, [stopIdleWatch, view.phase]);

  // TURN credentials are short-lived; renew them before they lapse rather
  // than after the call has already failed over to nothing.
  useEffect(() => {
    if (view.phase === 'ended') return;
    const timer = setInterval(() => {
      const msLeft = iceRef.current.expiresAt - Date.now();
      if (msLeft > 90_000) return;
      void api
        .refreshIce(roomId)
        .then((ice) => {
          iceRef.current = ice;
          peerRef.current?.updateIceServers(ice);
        })
        .catch(() => undefined);
    }, 30_000);
    return () => clearInterval(timer);
  }, [roomId, view.phase]);

  const secondsLeft = useMemo(() => {
    if (view.callEndsAt === null) return null;
    return Math.max(0, Math.round((view.callEndsAt - Date.now()) / 1000));
  }, [view.callEndsAt]);

  return {
    view,
    originalStream,
    translatedStream,
    secondsLeft,
    toggleMic,
    restartTranslator,
    wakeTranslator,
    leave,
  };
}
